import { GET_REGION } from '../state/queries/InstantHelp';
import { captureException } from '@sentry/react-native';
import { isApolloError, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useStores } from './useStores';

export interface Region {
    phoneLanguages: string[];
    freePhoneNumber: string;
    dialPhoneNumber: string;
    regionCode: string;
    localLanguagePhoneStartingHour: number;
    localLanguagePhoneEndHour: number;
    officeHourStartingHour: number;
    officeHourEndHour: number;
    utcTimezone: number;
    videocallLocalLanguage: string[]
    localLanguageAvailable: boolean;
    beginningOfTheWeek: number;
    endOfTheWeek: number;
  }

export interface  RegionQuery {
        regions: {
          items: Region[]
        }
      }

const DEFAULT_LANG = 'en_us';
export const useRegionInfo = () => {
  const { user, language } = useStores();

  const [error, setError] = useState<string | undefined>();
  const [languages, setLanguages] = useState<string[]>([]);
  const [videocallLanguages, setVideoCallLanguages] = useState<string[]>([]);
  const [videocallInfobox, setVideoCallInfobox] = useState<boolean>(false);
  const [regionData, setRegionData] = useState<Region>();
  const [phoneInAllLanguage, setPhoneInAllLanguage] = useState<boolean>(true);
  const [officeHour, setOfficeHour] = useState<boolean>(true);

  const query = useQuery(GET_REGION, {
    onCompleted: (data: RegionQuery) => {
      setError(undefined);
      const region = user.user?.region;
      const r = data.regions.items.find(item => item.regionCode === region);
      if(!r) {
        const errorMsg = `Region not found ${JSON.stringify(region)}`;
        console.error(errorMsg);
        captureException(errorMsg);
      } else {
        setRegionData(r as Region);
      }
    },
    onError: error => {
      console.error(error?.message || error);
      if (isApolloError(error)) {
        captureException(error);
      }
      setError(language.translate?.MOBILE?.ERROR?.GENERIC || 'Something went wrong...');
    },
    fetchPolicy: 'cache-and-network',
    variables: {
      criteria: {
        pagination: {
          page: 1,
          perPage: 300
        }
      }
    }
  });

  useEffect(() => {
    if(regionData?.phoneLanguages && regionData.phoneLanguages.length > 0) {
      setLanguages(regionData.phoneLanguages);
    } else {
      setLanguages([DEFAULT_LANG]);
    }
    if(officeHour) {
      console.log('during office hours', regionData?.videocallLocalLanguage);
      setVideoCallLanguages(regionData?.videocallLocalLanguage || [DEFAULT_LANG]);
    } else {
      console.log('outside office hours', DEFAULT_LANG);
      setVideoCallLanguages([DEFAULT_LANG]);
    }
  }
  , [regionData, officeHour]);

  useEffect(() => {
    // we're outside of office hours and usually (in office hours) the service includes
    // a local language then show an infobox explaining the situation
    console.log('localLanguageAvailable', regionData?.localLanguageAvailable);
    console.log('officeHour', officeHour);
    if(regionData?.localLanguageAvailable && !officeHour) {
      setVideoCallInfobox(true);
    } else {
      setVideoCallInfobox(false);
    }
  }, [regionData?.localLanguageAvailable, officeHour]);


  useEffect(() => {
    // start day to end day (e.g. 0-6 for sunday to saturday, 6-3 for saturday to wednesday)
    // start hour to end hour (e.g. 8-17 for 8am to 5pm)
    if(regionData) {
      const utcHours = new Date().getUTCHours();
      const utcWeekday = new Date().getUTCDay();
      const hourUncorrected = (utcHours + regionData.utcTimezone);
      // + 24 handles negative case, % 24 clamps between 0-23
      const currentHourInRegion = (hourUncorrected + 24) % 24;
      // weekday should increment or decrement if hour is outside of 0-23
      // increment beyond 6 should be 0, decrement below 0 should be 6
      // + 7 handles negative case, % 7 clamps between 0-6
      let weekday = (utcWeekday -1) % 7;
      if (hourUncorrected > 23) weekday = (weekday + 1 + 7) % 7;
      if (hourUncorrected < 0) weekday = (weekday - 1 + 7) % 7;

      const phoneStartHour = regionData.localLanguagePhoneStartingHour;
      const phoneEndHour =  regionData.localLanguagePhoneEndHour;
      const localLanguagesAvailableByPhone = currentHourInRegion >= phoneStartHour && currentHourInRegion < phoneEndHour;
      console.log('phoneStartHour', phoneStartHour, 'phoneEndHour', phoneEndHour, 'currentHourInRegion', currentHourInRegion, 'weekday', weekday, 'localLanguagesAvailableByPhone', localLanguagesAvailableByPhone);
      // TODO workdays for phone
      setPhoneInAllLanguage(localLanguagesAvailableByPhone);

      const videoStartHour = regionData.officeHourStartingHour;
      const videoEndHour = regionData.officeHourEndHour;
      const startDay = regionData.beginningOfTheWeek;
      const endDay =  regionData.endOfTheWeek;
      console.log('startDay', startDay, 'endDay', endDay, 'videoStartHour', videoStartHour, 'videoEndHour', videoEndHour, 'currentHourInRegion', currentHourInRegion, 'weekday', weekday);
      const isWorkday = (startDay === endDay) ||
        (endDay > startDay && weekday >= startDay && weekday <= endDay) ||
        (endDay < startDay && weekday <= endDay && weekday >= startDay);
      const isWorkhour = videoStartHour === videoEndHour ||
        (currentHourInRegion >= videoStartHour && currentHourInRegion < videoEndHour);
      console.log('isWorkday', isWorkday, 'isWorkhour', isWorkhour);
      setOfficeHour(isWorkday && isWorkhour);
    }
  }, [regionData]);

  return {
    languages,
    phoneInAllLanguage,
    freePhoneNumber: regionData?.freePhoneNumber,
    dialPhoneNumber: regionData?.dialPhoneNumber,
    officeHour,
    videocallLocalLanguages: regionData?.videocallLocalLanguage,
    localLanguageAvailable: regionData?.localLanguageAvailable,
    beginningOfTheWeek: regionData?.beginningOfTheWeek,
    endOfTheWeek: regionData?.endOfTheWeek,
    localLanguagePhoneStartingHour: regionData?.localLanguagePhoneStartingHour,
    localLanguagePhoneEndHour: regionData?.localLanguagePhoneEndHour,
    officeHourStartingHour: regionData?.officeHourStartingHour,
    officeHourEndHour: regionData?.officeHourEndHour,
    utcTimezone: regionData?.utcTimezone,
    videocallLanguages,
    videocallInfobox,
    loading: query.loading,
    error,
  };
};